import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNotification } from '../../../context/NotificationContext';
import { useMaintenance } from '../../../context/MaintenanceContext';
import axios from 'axios';
import { API_URL } from '../../../config/api';
import './WebsiteSettings.css';

const WebsiteSettings = () => {
  const { t } = useTranslation();
  const { showError, showSuccess } = useNotification();
  const { maintenanceMode, setMaintenanceMode, refetch } = useMaintenance();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [localMaintenance, setLocalMaintenance] = useState(false);
  const [restaurantCategories, setRestaurantCategories] = useState([]);
  const [storeCategories, setStoreCategories] = useState([]);
  const [newRestaurantCategoryFi, setNewRestaurantCategoryFi] = useState('');
  const [newRestaurantCategoryEn, setNewRestaurantCategoryEn] = useState('');
  const [newStoreCategoryFi, setNewStoreCategoryFi] = useState('');
  const [newStoreCategoryEn, setNewStoreCategoryEn] = useState('');
  const [editingRestaurantCategoryIndex, setEditingRestaurantCategoryIndex] = useState(null);
  const [editingRestaurantCategoryFi, setEditingRestaurantCategoryFi] = useState('');
  const [editingRestaurantCategoryEn, setEditingRestaurantCategoryEn] = useState('');
  const [editingStoreCategoryIndex, setEditingStoreCategoryIndex] = useState(null);
  const [editingStoreCategoryFi, setEditingStoreCategoryFi] = useState('');
  const [editingStoreCategoryEn, setEditingStoreCategoryEn] = useState('');
  const [savingCategories, setSavingCategories] = useState(false);
  const [generalTerms, setGeneralTerms] = useState('');
  const [generalTermsEn, setGeneralTermsEn] = useState('');
  const [savingGeneralTerms, setSavingGeneralTerms] = useState(false);
  const [gdprFi, setGdprFi] = useState('');
  const [gdprEn, setGdprEn] = useState('');
  const [savingGdpr, setSavingGdpr] = useState(false);
  const [guidelinesForReviewsFi, setGuidelinesForReviewsFi] = useState('');
  const [guidelinesForReviewsEn, setGuidelinesForReviewsEn] = useState('');
  const [savingGuidelinesReviews, setSavingGuidelinesReviews] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  useEffect(() => {
    setLocalMaintenance(maintenanceMode);
  }, [maintenanceMode]);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/settings`);
      setLocalMaintenance(!!response.data.maintenanceMode);
      const norm = (arr) => (Array.isArray(arr) ? arr : []).map((c) =>
        typeof c === 'string' ? { fi: c, en: c } : { fi: c?.fi ?? '', en: c?.en ?? '' }
      );
      setRestaurantCategories(norm(response.data.restaurantCategories));
      setStoreCategories(norm(response.data.storeCategories));
      setGeneralTerms(typeof response.data.generalTerms === 'string' ? response.data.generalTerms : '');
      setGeneralTermsEn(typeof response.data.generalTermsEn === 'string' ? response.data.generalTermsEn : '');
      setGdprFi(typeof response.data.gdprFi === 'string' ? response.data.gdprFi : '');
      setGdprEn(typeof response.data.gdprEn === 'string' ? response.data.gdprEn : '');
      setGuidelinesForReviewsFi(typeof response.data.guidelinesForReviewsFi === 'string' ? response.data.guidelinesForReviewsFi : '');
      setGuidelinesForReviewsEn(typeof response.data.guidelinesForReviewsEn === 'string' ? response.data.guidelinesForReviewsEn : '');
    } catch (error) {
      console.error('Error fetching website settings:', error);
      showError(t('admin.websiteSettingsLoadError') || 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleMaintenanceToggle = async () => {
    const newValue = !localMaintenance;
    setSaving(true);
    try {
      const response = await axios.patch(`${API_URL}/admin/settings`, {
        maintenanceMode: newValue
      });
      setLocalMaintenance(!!response.data.maintenanceMode);
      setMaintenanceMode(!!response.data.maintenanceMode);
      await refetch();
      showSuccess(
        response.data.maintenanceMode
          ? (t('admin.maintenanceModeOn') || 'Maintenance mode is now on.')
          : (t('admin.maintenanceModeOff') || 'Maintenance mode is now off.')
      );
    } catch (error) {
      showError(error.response?.data?.message || (t('admin.websiteSettingsSaveError') || 'Failed to save settings'));
    } finally {
      setSaving(false);
    }
  };

  const addRestaurantCategory = () => {
    const fi = (newRestaurantCategoryFi || '').trim();
    const en = (newRestaurantCategoryEn || '').trim();
    if (!fi && !en) return;
    const exists = restaurantCategories.some((c) => (c.fi || '').trim() === fi && (c.en || '').trim() === en);
    if (exists) return;
    setRestaurantCategories([...restaurantCategories, { fi: fi || en, en: en || fi }].sort((a, b) => (a.fi || a.en).localeCompare(b.fi || b.en)));
    setNewRestaurantCategoryFi('');
    setNewRestaurantCategoryEn('');
  };

  const removeRestaurantCategory = (index) => {
    setRestaurantCategories(restaurantCategories.filter((_, i) => i !== index));
    if (editingRestaurantCategoryIndex === index) setEditingRestaurantCategoryIndex(null);
    else if (editingRestaurantCategoryIndex != null && editingRestaurantCategoryIndex > index) setEditingRestaurantCategoryIndex(editingRestaurantCategoryIndex - 1);
  };

  const startEditRestaurantCategory = (index) => {
    const cat = restaurantCategories[index] || {};
    setEditingRestaurantCategoryIndex(index);
    setEditingRestaurantCategoryFi(cat.fi || '');
    setEditingRestaurantCategoryEn(cat.en || '');
  };

  const saveEditRestaurantCategory = () => {
    if (editingRestaurantCategoryIndex == null) return;
    const fi = (editingRestaurantCategoryFi || '').trim();
    const en = (editingRestaurantCategoryEn || '').trim();
    if (!fi && !en) return;
    const next = [...restaurantCategories];
    next[editingRestaurantCategoryIndex] = { fi: fi || en, en: en || fi };
    setRestaurantCategories(next.sort((a, b) => (a.fi || a.en).localeCompare(b.fi || b.en)));
    setEditingRestaurantCategoryIndex(null);
  };

  const cancelEditRestaurantCategory = () => {
    setEditingRestaurantCategoryIndex(null);
  };

  const addStoreCategory = () => {
    const fi = (newStoreCategoryFi || '').trim();
    const en = (newStoreCategoryEn || '').trim();
    if (!fi && !en) return;
    const exists = storeCategories.some((c) => (c.fi || '').trim() === fi && (c.en || '').trim() === en);
    if (exists) return;
    setStoreCategories([...storeCategories, { fi: fi || en, en: en || fi }].sort((a, b) => (a.fi || a.en).localeCompare(b.fi || b.en)));
    setNewStoreCategoryFi('');
    setNewStoreCategoryEn('');
  };

  const removeStoreCategory = (index) => {
    setStoreCategories(storeCategories.filter((_, i) => i !== index));
    if (editingStoreCategoryIndex === index) setEditingStoreCategoryIndex(null);
    else if (editingStoreCategoryIndex != null && editingStoreCategoryIndex > index) setEditingStoreCategoryIndex(editingStoreCategoryIndex - 1);
  };

  const startEditStoreCategory = (index) => {
    const cat = storeCategories[index] || {};
    setEditingStoreCategoryIndex(index);
    setEditingStoreCategoryFi(cat.fi || '');
    setEditingStoreCategoryEn(cat.en || '');
  };

  const saveEditStoreCategory = () => {
    if (editingStoreCategoryIndex == null) return;
    const fi = (editingStoreCategoryFi || '').trim();
    const en = (editingStoreCategoryEn || '').trim();
    if (!fi && !en) return;
    const next = [...storeCategories];
    next[editingStoreCategoryIndex] = { fi: fi || en, en: en || fi };
    setStoreCategories(next.sort((a, b) => (a.fi || a.en).localeCompare(b.fi || b.en)));
    setEditingStoreCategoryIndex(null);
  };

  const cancelEditStoreCategory = () => {
    setEditingStoreCategoryIndex(null);
  };

  const saveCategories = async () => {
    setSavingCategories(true);
    try {
      await axios.patch(`${API_URL}/admin/settings`, {
        restaurantCategories,
        storeCategories
      });
      showSuccess(t('admin.categoriesSaved') || 'Categories saved.');
    } catch (error) {
      showError(error.response?.data?.message || (t('admin.websiteSettingsSaveError') || 'Failed to save settings'));
    } finally {
      setSavingCategories(false);
    }
  };

  const saveGeneralTerms = async () => {
    setSavingGeneralTerms(true);
    try {
      const token = localStorage.getItem('token');
      const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
      const response = await axios.patch(`${API_URL}/admin/settings`, { generalTerms, generalTermsEn }, config);
      if (typeof response.data.generalTerms === 'string') setGeneralTerms(response.data.generalTerms);
      if (typeof response.data.generalTermsEn === 'string') setGeneralTermsEn(response.data.generalTermsEn);
      showSuccess(t('admin.generalTermsSaved') || 'General terms saved.');
    } catch (error) {
      showError(error.response?.data?.message || (t('admin.websiteSettingsSaveError') || 'Failed to save settings'));
    } finally {
      setSavingGeneralTerms(false);
    }
  };

  const saveGdpr = async () => {
    setSavingGdpr(true);
    try {
      const token = localStorage.getItem('token');
      const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
      const response = await axios.patch(`${API_URL}/admin/settings`, { gdprFi, gdprEn }, config);
      if (typeof response.data.gdprFi === 'string') setGdprFi(response.data.gdprFi);
      if (typeof response.data.gdprEn === 'string') setGdprEn(response.data.gdprEn);
      showSuccess(t('admin.gdprSaved') || 'GDPR / privacy statement saved.');
    } catch (error) {
      showError(error.response?.data?.message || (t('admin.websiteSettingsSaveError') || 'Failed to save settings'));
    } finally {
      setSavingGdpr(false);
    }
  };

  const saveGuidelinesReviews = async () => {
    setSavingGuidelinesReviews(true);
    try {
      const response = await axios.patch(`${API_URL}/admin/settings`, {
        guidelinesForReviewsFi,
        guidelinesForReviewsEn
      });
      if (typeof response.data.guidelinesForReviewsFi === 'string') setGuidelinesForReviewsFi(response.data.guidelinesForReviewsFi);
      if (typeof response.data.guidelinesForReviewsEn === 'string') setGuidelinesForReviewsEn(response.data.guidelinesForReviewsEn);
      showSuccess(t('admin.guidelinesReviewsSaved') || 'Guidelines for reviews saved.');
    } catch (error) {
      showError(error.response?.data?.message || (t('admin.websiteSettingsSaveError') || 'Failed to save settings'));
    } finally {
      setSavingGuidelinesReviews(false);
    }
  };

  if (loading) {
    return (
      <div className="website-settings-page">
        <p>{t('admin.loading')}</p>
      </div>
    );
  }

  return (
    <div className="website-settings-page">
      <div className="website-settings-header">
        <h1 className="website-settings-title">{t('admin.websiteSettings')}</h1>
      </div>
      <p className="website-settings-intro">{t('admin.websiteSettingsIntro')}</p>

      <div className="website-settings-card">
        <div className="website-settings-row">
          <div>
            <h3>{t('admin.maintenanceMode')}</h3>
            <p className="website-settings-hint">{t('admin.maintenanceModeHint')}</p>
          </div>
          <label className="website-settings-toggle">
            <input
              type="checkbox"
              checked={localMaintenance}
              onChange={handleMaintenanceToggle}
              disabled={saving}
            />
            <span className="website-settings-slider"></span>
          </label>
        </div>
        {localMaintenance && (
          <p className="website-settings-status website-settings-status-on">
            {t('admin.maintenanceModeActive')}
          </p>
        )}
      </div>

      <div className="website-settings-card website-settings-categories-card">
        <h3>{t('admin.restaurantCategories') || 'Restaurant categories'}</h3>
        <p className="website-settings-hint">{t('admin.restaurantCategoriesHint') || 'These categories appear as filters on the Restaurants page. You can assign a category to each restaurant in Restaurants management.'}</p>
        <p className="website-settings-hint">{t('admin.categoryFiEnHint') || 'Add both Finnish (FI) and English (EN) names so categories display in the user\'s language.'}</p>
        <div className="website-settings-category-list website-settings-category-list-bilingual">
          {restaurantCategories.map((cat, index) => (
            editingRestaurantCategoryIndex === index ? (
              <div key={`edit-rest-${index}`} className="website-settings-category-edit-row">
                <input
                  type="text"
                  value={editingRestaurantCategoryFi}
                  onChange={(e) => setEditingRestaurantCategoryFi(e.target.value)}
                  placeholder={t('admin.categoryFiPlaceholder') || 'FI name'}
                  className="website-settings-input website-settings-edit-input"
                  aria-label="Finnish name"
                />
                <input
                  type="text"
                  value={editingRestaurantCategoryEn}
                  onChange={(e) => setEditingRestaurantCategoryEn(e.target.value)}
                  placeholder={t('admin.categoryEnPlaceholder') || 'EN name'}
                  className="website-settings-input website-settings-edit-input"
                  aria-label="English name"
                />
                <button type="button" onClick={saveEditRestaurantCategory} className="website-settings-edit-save-btn" aria-label={t('admin.save') || 'Save'} title={t('admin.save') || 'Save'}>
                  <i className="fas fa-check" aria-hidden="true" />
                </button>
                <button type="button" onClick={cancelEditRestaurantCategory} className="website-settings-edit-cancel-btn" aria-label={t('admin.cancel') || 'Cancel'} title={t('admin.cancel') || 'Cancel'}>
                  <i className="fas fa-times" aria-hidden="true" />
                </button>
              </div>
            ) : (
              <span key={`${index}-${(cat.fi || '')}-${(cat.en || '')}`} className="website-settings-tag website-settings-tag-bilingual">
                <span className="website-settings-tag-fi">{(cat.fi || cat.en || '').trim() || '—'}</span>
                <span className="website-settings-tag-sep"> / </span>
                <span className="website-settings-tag-en">{(cat.en || cat.fi || '').trim() || '—'}</span>
                <button type="button" onClick={() => startEditRestaurantCategory(index)} className="website-settings-tag-edit" aria-label={t('admin.edit') || 'Edit'} title={t('admin.edit') || 'Edit'}>
                  <i className="fas fa-pen" aria-hidden="true" />
                </button>
                <button type="button" onClick={() => removeRestaurantCategory(index)} className="website-settings-tag-remove" aria-label={t('admin.remove') || 'Remove'} title={t('admin.remove') || 'Remove'}>×</button>
              </span>
            )
          ))}
        </div>
        <div className="website-settings-add-row website-settings-add-row-bilingual">
          <input
            type="text"
            value={newRestaurantCategoryFi}
            onChange={(e) => setNewRestaurantCategoryFi(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addRestaurantCategory())}
            placeholder={t('admin.categoryFiPlaceholder') || 'FI name'}
            className="website-settings-input"
            aria-label="Finnish name"
          />
          <input
            type="text"
            value={newRestaurantCategoryEn}
            onChange={(e) => setNewRestaurantCategoryEn(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addRestaurantCategory())}
            placeholder={t('admin.categoryEnPlaceholder') || 'EN name'}
            className="website-settings-input"
            aria-label="English name"
          />
          <button type="button" onClick={addRestaurantCategory} className="website-settings-add-btn">{t('admin.add') || 'Add'}</button>
        </div>
      </div>

      <div className="website-settings-card website-settings-categories-card">
        <h3>{t('admin.storeCategories') || 'Store categories'}</h3>
        <p className="website-settings-hint">{t('admin.storeCategoriesHint') || 'These categories appear as filters on the Stores page. You can assign a category to each store in Stores management.'}</p>
        <p className="website-settings-hint">{t('admin.categoryFiEnHint') || 'Add both Finnish (FI) and English (EN) names so categories display in the user\'s language.'}</p>
        <div className="website-settings-category-list website-settings-category-list-bilingual">
          {storeCategories.map((cat, index) => (
            editingStoreCategoryIndex === index ? (
              <div key={`edit-store-${index}`} className="website-settings-category-edit-row">
                <input
                  type="text"
                  value={editingStoreCategoryFi}
                  onChange={(e) => setEditingStoreCategoryFi(e.target.value)}
                  placeholder={t('admin.categoryFiPlaceholder') || 'FI name'}
                  className="website-settings-input website-settings-edit-input"
                  aria-label="Finnish name"
                />
                <input
                  type="text"
                  value={editingStoreCategoryEn}
                  onChange={(e) => setEditingStoreCategoryEn(e.target.value)}
                  placeholder={t('admin.categoryEnPlaceholder') || 'EN name'}
                  className="website-settings-input website-settings-edit-input"
                  aria-label="English name"
                />
                <button type="button" onClick={saveEditStoreCategory} className="website-settings-edit-save-btn" aria-label={t('admin.save') || 'Save'} title={t('admin.save') || 'Save'}>
                  <i className="fas fa-check" aria-hidden="true" />
                </button>
                <button type="button" onClick={cancelEditStoreCategory} className="website-settings-edit-cancel-btn" aria-label={t('admin.cancel') || 'Cancel'} title={t('admin.cancel') || 'Cancel'}>
                  <i className="fas fa-times" aria-hidden="true" />
                </button>
              </div>
            ) : (
              <span key={`${index}-${(cat.fi || '')}-${(cat.en || '')}`} className="website-settings-tag website-settings-tag-bilingual">
                <span className="website-settings-tag-fi">{(cat.fi || cat.en || '').trim() || '—'}</span>
                <span className="website-settings-tag-sep"> / </span>
                <span className="website-settings-tag-en">{(cat.en || cat.fi || '').trim() || '—'}</span>
                <button type="button" onClick={() => startEditStoreCategory(index)} className="website-settings-tag-edit" aria-label={t('admin.edit') || 'Edit'} title={t('admin.edit') || 'Edit'}>
                  <i className="fas fa-pen" aria-hidden="true" />
                </button>
                <button type="button" onClick={() => removeStoreCategory(index)} className="website-settings-tag-remove" aria-label={t('admin.remove') || 'Remove'} title={t('admin.remove') || 'Remove'}>×</button>
              </span>
            )
          ))}
        </div>
        <div className="website-settings-add-row website-settings-add-row-bilingual">
          <input
            type="text"
            value={newStoreCategoryFi}
            onChange={(e) => setNewStoreCategoryFi(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addStoreCategory())}
            placeholder={t('admin.categoryFiPlaceholder') || 'FI name'}
            className="website-settings-input"
            aria-label="Finnish name"
          />
          <input
            type="text"
            value={newStoreCategoryEn}
            onChange={(e) => setNewStoreCategoryEn(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addStoreCategory())}
            placeholder={t('admin.categoryEnPlaceholder') || 'EN name'}
            className="website-settings-input"
            aria-label="English name"
          />
          <button type="button" onClick={addStoreCategory} className="website-settings-add-btn">{t('admin.add') || 'Add'}</button>
        </div>
        <button type="button" onClick={saveCategories} disabled={savingCategories} className="website-settings-save-categories-btn">
          {savingCategories ? (t('admin.saving') || 'Saving...') : (t('admin.saveCategories') || 'Save categories')}
        </button>
      </div>

      <div className="website-settings-card website-settings-general-terms-card">
        <h3>{t('admin.generalTerms') || 'Yleiset ehdot'}</h3>
        <p className="website-settings-hint">{t('admin.generalTermsHint') || 'This text is shown on the General Terms page (link in footer). Use "## Heading" for section titles, and blank lines to separate paragraphs. One field per language.'}</p>
        <div className="website-settings-two-column">
          <div>
            <label className="website-settings-label">{t('admin.finnish') || 'Finnish (Suomi)'}</label>
            <textarea
              value={generalTerms}
              onChange={(e) => setGeneralTerms(e.target.value)}
              className="website-settings-textarea"
              rows={16}
              placeholder={t('admin.generalTermsPlaceholder') || 'Yleisten ehtojen teksti...'}
            />
          </div>
          <div>
            <label className="website-settings-label">{t('admin.english') || 'English'}</label>
            <textarea
              value={generalTermsEn}
              onChange={(e) => setGeneralTermsEn(e.target.value)}
              className="website-settings-textarea"
              rows={16}
              placeholder={t('admin.generalTermsPlaceholderEn') || 'General terms text (English)...'}
            />
          </div>
        </div>
        <button type="button" onClick={saveGeneralTerms} disabled={savingGeneralTerms} className="website-settings-save-categories-btn">
          {savingGeneralTerms ? (t('admin.saving') || 'Saving...') : (t('admin.saveGeneralTerms') || 'Save general terms')}
        </button>
      </div>

      <div className="website-settings-card website-settings-general-terms-card">
        <h3>{t('admin.gdpr') || 'GDPR / Tietosuojaseloste'}</h3>
        <p className="website-settings-hint">{t('admin.gdprHint') || 'Text shown on the GDPR / Privacy statement page (link in footer). Use "## Heading" for section titles, and blank lines to separate paragraphs. First paragraph can be used as intro. One field per language.'}</p>
        <div className="website-settings-two-column">
          <div>
            <label className="website-settings-label">{t('admin.finnish') || 'Finnish (Suomi)'}</label>
            <textarea
              value={gdprFi}
              onChange={(e) => setGdprFi(e.target.value)}
              className="website-settings-textarea"
              rows={20}
              placeholder={t('admin.gdprPlaceholder') || 'Tietosuojaseloste suomeksi...'}
            />
          </div>
          <div>
            <label className="website-settings-label">{t('admin.english') || 'English'}</label>
            <textarea
              value={gdprEn}
              onChange={(e) => setGdprEn(e.target.value)}
              className="website-settings-textarea"
              rows={20}
              placeholder={t('admin.gdprPlaceholderEn') || 'Privacy statement in English...'}
            />
          </div>
        </div>
        <button type="button" onClick={saveGdpr} disabled={savingGdpr} className="website-settings-save-categories-btn">
          {savingGdpr ? (t('admin.saving') || 'Saving...') : (t('admin.saveGdpr') || 'Save GDPR / privacy statement')}
        </button>
      </div>

      <div className="website-settings-card website-settings-general-terms-card">
        <h3>{t('admin.guidelinesForReviews') || 'Guidelines for reviews'}</h3>
        <p className="website-settings-hint">{t('admin.guidelinesForReviewsHint') || 'Text shown on the Guidelines for reviews page (link in footer). Use "## Heading" for section titles, and blank lines to separate paragraphs. One field per language.'}</p>
        <div className="website-settings-two-column">
          <div>
            <label className="website-settings-label">{t('admin.finnish') || 'Finnish (Suomi)'}</label>
            <textarea
              value={guidelinesForReviewsFi}
              onChange={(e) => setGuidelinesForReviewsFi(e.target.value)}
              className="website-settings-textarea"
              rows={20}
              placeholder={t('admin.guidelinesForReviewsPlaceholder') || 'Arviointiohjeet suomeksi...'}
            />
          </div>
          <div>
            <label className="website-settings-label">{t('admin.english') || 'English'}</label>
            <textarea
              value={guidelinesForReviewsEn}
              onChange={(e) => setGuidelinesForReviewsEn(e.target.value)}
              className="website-settings-textarea"
              rows={20}
              placeholder={t('admin.guidelinesForReviewsPlaceholderEn') || 'Guidelines for reviews in English...'}
            />
          </div>
        </div>
        <button type="button" onClick={saveGuidelinesReviews} disabled={savingGuidelinesReviews} className="website-settings-save-categories-btn">
          {savingGuidelinesReviews ? (t('admin.saving') || 'Saving...') : (t('admin.saveGuidelinesReviews') || 'Save guidelines for reviews')}
        </button>
      </div>
    </div>
  );
};

export default WebsiteSettings;
